import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaArrowLeft, FaArrowRight, FaFileExcel, FaFilePdf, FaPrint, FaSearch, FaSort, FaSortUp, FaSortDown, FaChevronDown, FaEyeSlash, FaColumns, FaGripVertical, FaUndo } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const INITIAL_COLUMNS = [
    { id: 'date', label: 'Date', visible: true, width: '120px' },
    { id: 'itemName', label: 'Item Name', visible: true, width: '180px' },
    { id: 'brand', label: 'Brand', visible: true, width: '120px' },
    { id: 'stockUom', label: 'Stock UOM', visible: true, width: '100px' },
    { id: 'openingQty', label: 'Opening Qty', visible: true, width: '120px', bgColor: '#f8fafc', color: '#64748b', align: 'right' },
    { id: 'inQty', label: 'In Qty', visible: true, width: '100px', bgColor: '#ecfdf5', color: '#059669', align: 'right' },
    { id: 'inValue', label: 'In Value', visible: true, width: '100px', bgColor: '#ecfdf5', color: '#059669', align: 'right' },
    { id: 'outQty', label: 'Out Qty', visible: true, width: '100px', bgColor: '#fef2f2', color: '#dc2626', align: 'right' },
    { id: 'outValue', label: 'Out Value', visible: true, width: '100px', bgColor: '#fef2f2', color: '#dc2626', align: 'right' },
    { id: 'balanceQty', label: 'Balance Qty', visible: true, width: '120px', bgColor: '#eff6ff', color: '#2563eb', align: 'right' },
    { id: 'balanceValue', label: 'Balance Value', visible: true, width: '120px', bgColor: '#1e293b', color: '#fff', align: 'right' },
    { id: 'valuationRate', label: 'Valuation Rate', visible: true, width: '120px', align: 'right' },
    { id: 'reservation', label: 'Reservation', visible: true, width: '120px', bgColor: '#fffbeb', color: '#d97706', align: 'right' },
    { id: 'availableQty', label: 'Available Qty', visible: true, width: '120px', bgColor: '#eff6ff', color: '#2563eb', align: 'right' },
    { id: 'company', label: 'Company', visible: true, width: '120px' }
];

const loadSavedColumns = () => {
    try {
        const saved = localStorage.getItem('stock_balance_column_order');
        if (saved) {
            const parsed = JSON.parse(saved);
            const ordered = [];
            parsed.forEach(p => {
                const colId = typeof p === 'string' ? p : p.id;
                const found = INITIAL_COLUMNS.find(c => c.id === colId);
                if (found) {
                    ordered.push({ ...found, visible: typeof p.visible === 'boolean' ? p.visible : found.visible });
                }
            });
            // Append any columns from INITIAL_COLUMNS that are not yet in saved order
            INITIAL_COLUMNS.forEach(c => {
                if (!ordered.some(o => o.id === c.id)) {
                    ordered.push(c);
                }
            });
            return ordered;
        }
    } catch (e) {
        console.error("Failed to load saved column order", e);
    }
    return INITIAL_COLUMNS;
};

const StockBalancePage = () => {
    const [balances, setBalances] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Filtering States
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [filterCompany, setFilterCompany] = useState('');
    const [filterGroup, setFilterGroup] = useState('');
    const [filterItem, setFilterItem] = useState('');
    const [filterUom, setFilterUom] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'itemName', direction: 'ascending' });

    // Column Management & Reorder States
    const [columns, setColumns] = useState(loadSavedColumns);
    const [draggedColId, setDraggedColId] = useState(null);
    const [dragOverColId, setDragOverColId] = useState(null);
    const [activeColumnMenu, setActiveColumnMenu] = useState(null);
    const [showColumnManager, setShowColumnManager] = useState(false);

    const navigate = useNavigate();
    const menuRef = useRef(null);
    const managerRef = useRef(null);
    const draggedColRef = useRef(null);

    // Save column order & visibility to localStorage
    const saveColumns = (newCols) => {
        setColumns(newCols);
        try {
            localStorage.setItem('stock_balance_column_order', JSON.stringify(newCols.map(c => ({ id: c.id, visible: c.visible }))));
        } catch (e) {
            console.error("Failed to save column order", e);
        }
    };

    // Reset columns to factory default
    const resetColumns = () => {
        try {
            localStorage.removeItem('stock_balance_column_order');
        } catch (e) {}
        setColumns(INITIAL_COLUMNS);
    };

    // Move column Left (-1) or Right (+1) among visible columns
    const moveColumn = (colId, direction) => {
        const visibleCols = columns.filter(c => c.visible);
        const visibleIndex = visibleCols.findIndex(c => c.id === colId);
        if (visibleIndex === -1) return;
        const targetVisibleIndex = visibleIndex + direction;
        if (targetVisibleIndex < 0 || targetVisibleIndex >= visibleCols.length) return;

        const targetColId = visibleCols[targetVisibleIndex].id;
        const fromIdx = columns.findIndex(c => c.id === colId);
        const toIdx = columns.findIndex(c => c.id === targetColId);
        if (fromIdx === -1 || toIdx === -1) return;

        const newCols = [...columns];
        const [moved] = newCols.splice(fromIdx, 1);
        newCols.splice(toIdx, 0, moved);
        saveColumns(newCols);
    };

    // Drag-and-drop handlers
    const handleDragStart = (e, colId) => {
        draggedColRef.current = colId;
        setDraggedColId(colId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', colId);
    };

    const handleDragOver = (e, colId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (draggedColRef.current && colId !== dragOverColId) {
            setDragOverColId(colId);
        }
    };

    const handleDrop = (e, targetColId) => {
        e.preventDefault();
        const sourceColId = draggedColRef.current || e.dataTransfer.getData('text/plain');
        if (!sourceColId || sourceColId === targetColId) {
            draggedColRef.current = null;
            setDraggedColId(null);
            setDragOverColId(null);
            return;
        }

        const fromIdx = columns.findIndex(c => c.id === sourceColId);
        const toIdx = columns.findIndex(c => c.id === targetColId);
        if (fromIdx !== -1 && toIdx !== -1) {
            const newCols = [...columns];
            const [moved] = newCols.splice(fromIdx, 1);
            newCols.splice(toIdx, 0, moved);
            saveColumns(newCols);
        }
        draggedColRef.current = null;
        setDraggedColId(null);
        setDragOverColId(null);
    };

    const handleDragEnd = () => {
        draggedColRef.current = null;
        setDraggedColId(null);
        setDragOverColId(null);
    };

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) setActiveColumnMenu(null);
            if (managerRef.current && !managerRef.current.contains(event.target)) setShowColumnManager(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getHeaders = () => {
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;
        const token = localStorage.getItem('token');

        const headers = { 
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        };

        const companyHeader = user?.company_name || user?.company || '';
        if (companyHeader) headers['X-Company-Name'] = companyHeader;

        const branchHeader = localStorage.getItem('active_branch') || user?.branch_name || user?.branch || '';
        if (branchHeader) headers['X-Branch-Name'] = branchHeader;

        return headers;
    };

    const fetchBalances = async () => {
        setLoading(true);
        try {
            const params = {};
            if (fromDate) params.fromDate = fromDate;
            if (toDate) params.toDate = toDate;

            const res = await axios.get('/api/inventory/stock-balance', { params, headers: getHeaders() });
            const validData = res.data.filter(entry => 
                (entry.itemName || entry.item_name || entry.name) && 
                (entry.itemName !== 'Unknown Item' && entry.item_name !== 'Unknown Item' && entry.name !== 'Unknown Item')
            );
            setBalances(validData);
        } catch (err) {
            console.error("Error fetching stock balances", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBalances();
    }, [fromDate, toDate]);

    // ─── Extract dropdown options ───
    const uniqueCompanies = [...new Set(balances.map(entry => entry.company || '').filter(Boolean))];
    const uniqueGroups = [...new Set(balances.map(entry => entry.brand || entry.category || '').filter(Boolean))];
    const uniqueItems = [...new Set(balances.map(entry => entry.itemName || entry.item_name || entry.name || '').filter(Boolean))];
    const uniqueUoms = [...new Set(balances.map(entry => entry.stockUom || entry.unit || '').filter(Boolean))];

    // ─── Filtering & Sorting Logic ───
    let processedBalances = balances.filter(entry => {
        const companyMatch = filterCompany ? (entry.company || '') === filterCompany : true;
        const groupMatch = filterGroup ? (entry.brand || entry.category || '') === filterGroup : true;
        const itemName = entry.itemName || entry.item_name || entry.name || '';
        const itemMatch = filterItem ? itemName === filterItem : true;
        const uom = entry.stockUom || entry.unit || '';
        const uomMatch = filterUom ? uom === filterUom : true;
        return companyMatch && groupMatch && itemMatch && uomMatch;
    });

    processedBalances.sort((a, b) => {
        if (!sortConfig.key) return 0;
        let aVal = a[sortConfig.key] || 0;
        let bVal = b[sortConfig.key] || 0;

        if (sortConfig.key === 'itemName') { aVal = a.itemName || a.item_name || a.name || ''; bVal = b.itemName || b.item_name || b.name || ''; }
        if (sortConfig.key === 'stockUom') { aVal = a.stockUom || a.unit || ''; bVal = b.stockUom || b.unit || ''; }

        if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }

        if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
    });

    // ─── Summary Calculations ───
    const summary = {
        totalItems: processedBalances.length,
        totalOpening: processedBalances.reduce((sum, item) => sum + (item.openingQty || item.opening_qty || 0), 0),
        totalIn: processedBalances.reduce((sum, item) => sum + (item.inQty || item.purchase_qty || 0), 0),
        totalOut: processedBalances.reduce((sum, item) => sum + (item.outQty || item.sale_qty || 0), 0),
        totalCurrentStock: processedBalances.reduce((sum, item) => sum + (item.balanceQty || item.current_stock || item.current_qty || 0), 0),
        totalStockValue: processedBalances.reduce((sum, item) => sum + (item.balanceValue || item.stock_value || 0), 0)
    };

    // ─── Export helpers ────────────────────────────────────────────────────────
    const exportToExcel = () => {
        const visibleCols = columns.filter(c => c.visible);
        const exportData = processedBalances.map(row => {
            const expRow = {};
            visibleCols.forEach(c => {
                if(c.id === 'date') expRow[c.label] = row.date || '-';
                else if(c.id === 'itemName') expRow[c.label] = row.itemName || row.name || row.item_name || '-';
                else if(c.id === 'stockUom') expRow[c.label] = row.stockUom || row.unit || '-';
                else if(c.id === 'openingQty') expRow[c.label] = Number(row.openingQty || row.opening_qty || 0).toFixed(2);
                else if(c.id === 'inQty') expRow[c.label] = Number(row.inQty || row.purchase_qty || 0).toFixed(2);
                else if(c.id === 'outQty') expRow[c.label] = Number(row.outQty || row.sale_qty || 0).toFixed(2);
                else if(c.id === 'balanceQty') expRow[c.label] = Number(row.balanceQty || row.current_stock || row.current_qty || 0).toFixed(2);
                else if(c.id === 'valuationRate') expRow[c.label] = Number(row.valuationRate || row.average_cost || 0).toFixed(4);
                else if(c.id === 'availableQty') expRow[c.label] = Number(row.availableQty || (row.balanceQty || row.current_stock || row.current_qty || 0)).toFixed(2);
                else if(['inValue', 'outValue', 'balanceValue', 'reservation'].includes(c.id)) expRow[c.label] = Number(row[c.id] || row.stock_value || 0).toFixed(2);
                else expRow[c.label] = row[c.id] || '-';
            });
            return expRow;
        });
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Balance");
        XLSX.writeFile(workbook, "Stock_Balance.xlsx");
    };

    const exportToPDF = () => {
        const doc = new jsPDF('landscape');
        doc.text("Stock Balance Report", 14, 15);
        const visibleCols = columns.filter(c => c.visible);
        const tableColumn = visibleCols.map(c => c.label);
        
        const tableRows = processedBalances.map(row => {
            return visibleCols.map(c => {
                if(c.id === 'date') return row.date || '-';
                if(c.id === 'itemName') return row.itemName || row.name || row.item_name || '-';
                if(c.id === 'stockUom') return row.stockUom || row.unit || '-';
                if(c.id === 'openingQty') return Number(row.openingQty || row.opening_qty || 0).toFixed(2);
                if(c.id === 'inQty') return Number(row.inQty || row.purchase_qty || 0).toFixed(2);
                if(c.id === 'outQty') return Number(row.outQty || row.sale_qty || 0).toFixed(2);
                if(c.id === 'balanceQty') return Number(row.balanceQty || row.current_stock || row.current_qty || 0).toFixed(2);
                if(c.id === 'valuationRate') return Number(row.valuationRate || row.average_cost || 0).toFixed(2);
                if(c.id === 'availableQty') return Number(row.availableQty || (row.balanceQty || row.current_stock || row.current_qty || 0)).toFixed(2);
                if(['inValue', 'outValue', 'balanceValue', 'reservation'].includes(c.id)) return Number(row[c.id] || row.stock_value || 0).toFixed(2);
                return row[c.id] || '-';
            });
        });
        
        doc.autoTable({ 
            head: [tableColumn], 
            body: tableRows, 
            startY: 20, 
            theme: 'grid', 
            headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] }, 
            styles: { fontSize: 5, overflow: 'linebreak', cellPadding: 1 } 
        });
        doc.save("Stock_Balance.pdf");
    };

    // ─── Table Cell Renderer ───
    const renderCell = (row, colId) => {
        switch(colId) {
            case 'date': return <div style={{ color: '#64748b', fontSize: '13px' }}>{!row.date || row.date === 'None to None' ? 'All Time' : row.date}</div>;
            case 'itemName': return <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px' }}>{row.itemName || row.name || row.item_name}</div>;
            case 'brand':
            case 'company': return <div style={{ color: '#64748b', fontSize: '13px' }}>{row[colId] || '-'}</div>;
            case 'stockUom': return <div style={{ color: '#64748b', fontSize: '13px', textTransform: 'uppercase' }}>{row.stockUom || row.unit || '-'}</div>;
            
            case 'openingQty': return <div style={{ fontWeight: '600', color: '#64748b', textAlign: 'right' }}>{Number(row.openingQty || row.opening_qty || 0).toFixed(2)}</div>;
            case 'inQty': return <div style={{ fontWeight: '600', color: '#10b981', textAlign: 'right' }}>{Number(row.inQty || row.purchase_qty || 0).toFixed(2)}</div>;
            case 'inValue': return <div style={{ fontWeight: '500', color: '#059669', textAlign: 'right' }}>₹ {Number(row.inValue || 0).toFixed(2)}</div>;
            case 'outQty': return <div style={{ fontWeight: '600', color: '#ef4444', textAlign: 'right' }}>{Number(row.outQty || row.sale_qty || 0).toFixed(2)}</div>;
            case 'outValue': return <div style={{ fontWeight: '500', color: '#dc2626', textAlign: 'right' }}>₹ {Number(row.outValue || 0).toFixed(2)}</div>;
            
            case 'balanceQty': 
                return (
                    <div style={{ fontWeight: '700', color: '#2563eb', fontSize: '14px', textAlign: 'right' }}>
                        {Number(row.balanceQty || row.current_stock || row.current_qty || 0).toFixed(2)}
                        {(row.stockUom || row.unit) === row.masterUnit && row.total_units_per_box && row.nosUnit && row.nosUnit !== row.masterUnit && (
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500', marginTop: '4px' }}>
                                ({((row.balanceQty || row.current_stock || row.current_qty || 0) * row.total_units_per_box).toFixed(2)} {row.nosUnit})
                            </div>
                        )}
                    </div>
                );
                
            case 'balanceValue': return <div style={{ fontWeight: '700', color: '#10b981', textAlign: 'right' }}>₹ {Number(row.balanceValue || row.stock_value || 0).toFixed(2)}</div>;
            case 'valuationRate': return <div style={{ fontWeight: '500', color: '#475569', textAlign: 'right' }}>₹ {Number(row.valuationRate || row.average_cost || 0).toFixed(4)}</div>;
            case 'reservation': return <div style={{ fontWeight: '600', color: '#d97706', textAlign: 'right' }}>{Number(row.reservation || 0).toFixed(2)}</div>;
            case 'availableQty': return <div style={{ fontWeight: '700', color: '#2563eb', textAlign: 'right' }}>{Number(row.availableQty || (row.balanceQty || row.current_stock || row.current_qty || 0)).toFixed(2)}</div>;
            default: return null;
        }
    };

    return (
        <div style={{ backgroundColor: "#F4F7FC", height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: "'Outfit', sans-serif", color: "#2B2D42" }}>
            
            {/* Header Area */}
            <div className="no-print" style={{ padding: '24px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', borderBottom: '1px solid #E2E8F0', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <button onClick={() => navigate(-1)} style={{ background: '#f8fafc', border: '1px solid #E2E8F0', padding: '10px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '600', color: '#475569', transition: '0.2s' }}>
                        <FaArrowLeft /> Back
                    </button>
                    <div>
                        <h2 style={{ margin: '0', fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>Stock Balance</h2>
                        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>Real-time inventory valuation</p>
                    </div>
                </div>
                
                <div style={{ display: 'flex', gap: '12px', position: 'relative' }}>
                    {/* Add Columns Button */}
                    <div ref={managerRef} style={{ position: 'relative' }}>
                        <button onClick={() => setShowColumnManager(!showColumnManager)} className="export-btn" style={{ background: '#f8fafc' }}>
                            <FaColumns style={{ color: '#3b82f6' }} /> Columns
                        </button>
                        {showColumnManager && (
                            <div className="column-manager-dropdown" style={{ width: '270px' }}>
                                <div style={{ padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>Manage & Reorder</span>
                                    <button onClick={resetColumns} title="Reset to default column order" style={{ background: 'transparent', border: 'none', color: '#3b82f6', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}>
                                        <FaUndo style={{ fontSize: '10px' }} /> Reset
                                    </button>
                                </div>
                                <div style={{ maxHeight: '280px', overflowY: 'auto', padding: '6px 0' }}>
                                    {columns.map((col, cIdx) => (
                                        <div key={col.id} className="column-manager-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 14px' }}>
                                            <div 
                                                style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1, minWidth: 0 }}
                                                onClick={() => {
                                                    const newCols = columns.map(c => c.id === col.id ? { ...c, visible: !c.visible } : c);
                                                    saveColumns(newCols);
                                                }}
                                            >
                                                <input type="checkbox" checked={col.visible} readOnly style={{ cursor: 'pointer' }} />
                                                <span style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{col.label}</span>
                                            </div>
                                            <div style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
                                                <button 
                                                    disabled={cIdx === 0} 
                                                    onClick={(e) => { e.stopPropagation(); moveColumn(col.id, -1); }}
                                                    style={{ border: '1px solid #e2e8f0', background: cIdx === 0 ? '#f8fafc' : '#ffffff', borderRadius: '4px', padding: '2px 6px', cursor: cIdx === 0 ? 'not-allowed' : 'pointer', opacity: cIdx === 0 ? 0.35 : 1, fontSize: '11px', fontWeight: 'bold' }}
                                                    title="Move Column Left"
                                                >
                                                    ←
                                                </button>
                                                <button 
                                                    disabled={cIdx === columns.length - 1} 
                                                    onClick={(e) => { e.stopPropagation(); moveColumn(col.id, 1); }}
                                                    style={{ border: '1px solid #e2e8f0', background: cIdx === columns.length - 1 ? '#f8fafc' : '#ffffff', borderRadius: '4px', padding: '2px 6px', cursor: cIdx === columns.length - 1 ? 'not-allowed' : 'pointer', opacity: cIdx === columns.length - 1 ? 0.35 : 1, fontSize: '11px', fontWeight: 'bold' }}
                                                    title="Move Column Right"
                                                >
                                                    →
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <button onClick={exportToExcel} className="export-btn no-print"><FaFileExcel style={{ color: '#107c41', fontSize: '16px' }} /> Excel</button>
                    <button onClick={exportToPDF} className="export-btn no-print"><FaFilePdf style={{ color: '#d32f2f', fontSize: '16px' }} /> PDF</button>
                    <button onClick={() => window.print()} className="export-btn no-print"><FaPrint style={{ color: '#475569', fontSize: '16px' }} /> Print</button>
                </div>
            </div>

            {/* Premium Filter Bar exactly matching the user screenshot */}
            <div className="no-print" style={{ background: '#ffffff', padding: '20px 32px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', borderBottom: '1px solid #E2E8F0' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Company Name</label>
                    <select className="premium-filter-input" value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}>
                        <option value="">Filter Company Name...</option>
                        {uniqueCompanies.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>From Date</label>
                    <input type="date" className="premium-filter-input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} title="From Date" />
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>To Date</label>
                    <input type="date" className="premium-filter-input" value={toDate} onChange={(e) => setToDate(e.target.value)} title="To Date" />
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Item Group</label>
                    <select className="premium-filter-input" value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
                        <option value="">Filter Item Group...</option>
                        {uniqueGroups.map((g, i) => <option key={i} value={g}>{g}</option>)}
                    </select>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Items</label>
                    <select className="premium-filter-input" value={filterItem} onChange={(e) => setFilterItem(e.target.value)}>
                        <option value="">Filter Items...</option>
                        {uniqueItems.map((item, i) => <option key={i} value={item}>{item}</option>)}
                    </select>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Stock UOM</label>
                    <select className="premium-filter-input" value={filterUom} onChange={(e) => setFilterUom(e.target.value)}>
                        <option value="">Filter Stock UOM...</option>
                        {uniqueUoms.map((uom, i) => <option key={i} value={uom}>{uom}</option>)}
                    </select>
                </div>
            </div>
            
            {/* Summary Cards */}
            <div className="no-print" style={{ padding: '24px 32px 0 32px', display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '16px' }}>
                <div className="summary-card">
                    <span className="card-label">Total Items</span>
                    <span className="card-value" style={{ color: '#3b82f6' }}>{summary.totalItems}</span>
                </div>
                <div className="summary-card">
                    <span className="card-label">Opening Qty</span>
                    <span className="card-value" style={{ color: '#64748b' }}>{summary.totalOpening.toFixed(2)}</span>
                </div>
                <div className="summary-card">
                    <span className="card-label">In Qty</span>
                    <span className="card-value" style={{ color: '#10b981' }}>{summary.totalIn.toFixed(2)}</span>
                </div>
                <div className="summary-card">
                    <span className="card-label">Out Qty</span>
                    <span className="card-value" style={{ color: '#ef4444' }}>{summary.totalOut.toFixed(2)}</span>
                </div>
                <div className="summary-card" style={{ border: '1px solid #3b82f6', background: '#eff6ff' }}>
                    <span className="card-label" style={{ color: '#1d4ed8' }}>Balance Qty</span>
                    <span className="card-value" style={{ color: '#1d4ed8' }}>{summary.totalCurrentStock.toFixed(2)}</span>
                </div>
                <div className="summary-card" style={{ background: '#1e293b' }}>
                    <span className="card-label" style={{ color: '#94a3b8' }}>Total Balance Value</span>
                    <span className="card-value" style={{ color: '#10b981' }}>₹ {summary.totalStockValue.toFixed(2)}</span>
                </div>
            </div>

            {/* Table Area */}
            <div style={{ padding: '24px 32px', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <div style={{ background: '#ffffff', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
                        
                        {/* Print Only Header */}
                        <div className="print-only" style={{ textAlign: 'center', marginBottom: '16px' }}>
                            <h2 style={{ fontSize: '18pt', margin: 0, padding: 0, color: '#000', fontWeight: 'bold' }}>Stock Balance Report</h2>
                            <p style={{ margin: '4px 0 0 0', padding: 0, fontSize: '10pt', color: '#000' }}>Date: {new Date().toLocaleDateString()}</p>
                        </div>

                        {loading ? (
                            <div style={{ padding: '64px', display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
                                <div className="loader"></div>
                                <p style={{ marginTop: '16px', color: '#64748b' }}>Calculating stock balances...</p>
                            </div>
                        ) : processedBalances.length === 0 ? (
                            <div style={{ padding: '64px', textAlign: 'center', color: '#94a3b8', fontSize: '16px', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <p>No stock data found for the current filters.</p>
                            </div>
                        ) : (
                            <table className="premium-table">
                                <thead>
                                    <tr>
                                        {columns.filter(c => c.visible).map((col, idx, arr) => (
                                            <th 
                                                key={col.id} 
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, col.id)}
                                                onDragOver={(e) => handleDragOver(e, col.id)}
                                                onDrop={(e) => handleDrop(e, col.id)}
                                                onDragEnd={handleDragEnd}
                                                className={`col-draggable-th ${draggedColId === col.id ? 'col-is-dragging' : ''} ${dragOverColId === col.id ? 'col-drag-over' : ''}`}
                                                style={{ background: col.bgColor || '#ffffff', color: col.color || '#475569', minWidth: col.width, textAlign: col.align || 'left' }}
                                                title="Drag to reorder column left or right"
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: col.align === 'right' ? 'flex-end' : 'space-between', gap: '6px' }}>
                                                    <span className="drag-handle no-print" title="Drag column left/right">
                                                        <FaGripVertical />
                                                    </span>

                                                    <div 
                                                        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: col.align === 'right' ? 'none' : 1, order: col.align === 'right' ? 2 : 1, marginLeft: col.align === 'right' ? '6px' : 0 }}
                                                        onClick={() => setSortConfig(s => ({ key: col.id, direction: s.key === col.id && s.direction === 'ascending' ? 'descending' : 'ascending' }))}
                                                    >
                                                        {col.label} 
                                                    </div>
                                                    
                                                    {/* Dropdown Menu Trigger */}
                                                    <div className="col-menu-trigger" style={{ order: col.align === 'right' ? 1 : 2 }} onClick={(e) => { e.stopPropagation(); setActiveColumnMenu(activeColumnMenu === col.id ? null : col.id); }}>
                                                        <FaChevronDown />
                                                    </div>

                                                    {/* Dropdown Menu */}
                                                    {activeColumnMenu === col.id && (
                                                        <div className="col-menu-dropdown" ref={menuRef} style={{ left: col.align === 'right' ? 'auto' : '0', right: col.align === 'right' ? '0' : 'auto' }} onClick={(e) => e.stopPropagation()}>
                                                            {/* Move Left / Right Options */}
                                                            {idx > 0 && (
                                                                <div className="col-menu-item" onClick={() => { moveColumn(col.id, -1); setActiveColumnMenu(null); }}>
                                                                    <FaArrowLeft style={{ color: '#3b82f6' }} /> Move Left
                                                                </div>
                                                            )}
                                                            {idx < arr.length - 1 && (
                                                                <div className="col-menu-item" onClick={() => { moveColumn(col.id, 1); setActiveColumnMenu(null); }}>
                                                                    <FaArrowRight style={{ color: '#3b82f6' }} /> Move Right
                                                                </div>
                                                            )}
                                                            {(idx > 0 || idx < arr.length - 1) && <div className="col-menu-divider"></div>}

                                                            <div className="col-menu-item" onClick={() => { setSortConfig({ key: col.id, direction: 'ascending' }); setActiveColumnMenu(null); }}>
                                                                <FaSortUp /> Sort Ascending
                                                            </div>
                                                            <div className="col-menu-item" onClick={() => { setSortConfig({ key: col.id, direction: 'descending' }); setActiveColumnMenu(null); }}>
                                                                <FaSortDown /> Sort Descending
                                                            </div>
                                                            <div className="col-menu-item" onClick={() => { if(sortConfig.key === col.id) setSortConfig({key: null, direction: null}); setActiveColumnMenu(null); }}>
                                                                <FaSort style={{ opacity: 0.5 }} /> Reset sorting
                                                            </div>
                                                            <div className="col-menu-divider"></div>
                                                            <div className="col-menu-item text-red" onClick={() => {
                                                                const newCols = columns.map(c => c.id === col.id ? { ...c, visible: false } : c);
                                                                saveColumns(newCols);
                                                                setActiveColumnMenu(null);
                                                            }}>
                                                                <FaEyeSlash /> Remove column
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {processedBalances.map((row, i) => (
                                        <tr key={i}>
                                            {columns.filter(c => c.visible).map(col => (
                                                <td key={col.id} style={{ background: col.id === 'openingQty' ? '#f8fafc' : col.id === 'inQty' || col.id === 'inValue' ? '#ecfdf5' : col.id === 'outQty' || col.id === 'outValue' ? '#fef2f2' : col.id === 'balanceQty' || col.id === 'availableQty' ? '#eff6ff' : col.id === 'reservation' ? '#fffbeb' : 'inherit' }}>
                                                    {renderCell(row, col.id)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
                
                .export-btn { background: #ffffff; border: 1px solid #E2E8F0; padding: 8px 16px; border-radius: 8px; display: flex; align-items: center; gap: 8px; cursor: pointer; font-weight: 600; color: #475569; transition: all 0.2s; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
                .export-btn:hover { background: #f8fafc; border-color: #cbd5e1; transform: translateY(-1px); }
                
                .summary-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
                .summary-card .card-label { font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; display: block; }
                .summary-card .card-value { font-size: 24px; font-weight: 700; display: block; line-height: 1; }

                .premium-table { width: 100%; border-collapse: collapse; border: 1px solid #000000; }
                .premium-table th { font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; padding: 14px 16px; text-align: left; position: sticky; top: 0; z-index: 10; border: 1px solid #000000; white-space: nowrap; user-select: none; }
                .premium-table td { padding: 14px 16px; color: #1e293b; font-size: 13px; font-weight: 500; border: 1px solid #000000; white-space: nowrap; }
                .premium-table tr:hover td { filter: brightness(0.95); }
                
                /* Column Drag and Drop Styles */
                .col-draggable-th { cursor: grab; transition: background-color 0.15s, border-color 0.15s; }
                .col-draggable-th:hover { filter: brightness(0.97); }
                .col-draggable-th:active { cursor: grabbing; }
                .col-is-dragging { opacity: 0.4; }
                .col-drag-over { border-left: 4px solid #2563eb !important; background: #e0f2fe !important; }
                .drag-handle { opacity: 0.35; cursor: grab; font-size: 11px; display: flex; align-items: center; }
                .drag-handle:hover { opacity: 1; color: #2563eb; }
                
                /* Print-only elements */
                .print-only { display: none; }

                /* Premium Grid Filter Styles */
                .premium-filter-input {
                    width: 100%;
                    padding: 10px 14px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 13px;
                    color: #64748b;
                    background: #ffffff;
                    outline: none;
                    font-family: inherit;
                    transition: all 0.2s;
                    appearance: none;
                    -webkit-appearance: none;
                }
                select.premium-filter-input {
                    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
                    background-repeat: no-repeat;
                    background-position: right 12px center;
                    background-size: 14px;
                }
                .premium-filter-input:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                    color: #0f172a;
                }
                input[type="date"].premium-filter-input::-webkit-datetime-edit { color: #64748b; }
                input[type="date"].premium-filter-input:focus::-webkit-datetime-edit { color: #0f172a; }
                input[type="date"].premium-filter-input::-webkit-calendar-picker-indicator { cursor: pointer; opacity: 0.6; }
                input[type="date"].premium-filter-input::-webkit-calendar-picker-indicator:hover { opacity: 1; }
                
                /* Column Menu Styles */
                .col-menu-trigger { padding: 4px; border-radius: 4px; color: inherit; opacity: 0.4; cursor: pointer; transition: 0.2s; display: flex; align-items: center; justify-content: center; }
                .col-menu-trigger:hover { opacity: 1; background: rgba(0,0,0,0.05); }
                
                .col-menu-dropdown { position: absolute; top: calc(100% - 8px); background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 10px 25px rgba(0,0,0,0.1); width: 200px; z-index: 50; overflow: hidden; padding: 4px 0; animation: fadeInDown 0.2s ease; }
                .col-menu-item { padding: 10px 16px; font-size: 13px; color: #334155; display: flex; align-items: center; gap: 10px; cursor: pointer; font-weight: 500; text-transform: none; letter-spacing: 0; transition: background 0.1s; }
                .col-menu-item:hover { background: #f1f5f9; }
                .col-menu-item.text-red { color: #ef4444; }
                .col-menu-item.text-red:hover { background: #fef2f2; }
                .col-menu-divider { height: 1px; background: #e2e8f0; margin: 4px 0; }
                
                /* Column Manager Dropdown */
                .column-manager-dropdown { position: absolute; top: calc(100% + 8px); right: 0; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.1); width: 240px; z-index: 50; overflow: hidden; animation: fadeInDown 0.2s ease; }
                .column-manager-item { padding: 8px 16px; display: flex; align-items: center; gap: 12px; cursor: pointer; transition: 0.1s; font-size: 13px; color: #475569; font-weight: 500; text-transform: none; }
                .column-manager-item:hover { background: #f8fafc; color: #0f172a; }
                .column-manager-item input { accent-color: #3b82f6; width: 16px; height: 16px; }

                .loader { width: 36px; height: 36px; border: 3px solid #e2e8f0; border-top: 3px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @media print {
                    .no-print { display: none !important; }
                    .print-only { display: block !important; }
                    
                    /* Disable all flexbox/grid layouts in print to force full width block flow */
                    body, html, #root, div { display: block !important; position: static !important; width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; background: transparent !important; border: none !important; box-shadow: none !important; overflow: visible !important; height: auto !important; }
                    
                    /* Restore padding specifically for the print header */
                    .print-only { margin-bottom: 20px !important; text-align: center !important; }
                    .print-only h2 { margin: 0 !important; padding: 0 !important; font-size: 16pt !important; color: #000 !important; font-weight: bold !important; }
                    
                    /* Force the table to stretch completely across the page with no gaps */
                    .premium-table { display: table !important; width: 100% !important; min-width: 100% !important; max-width: 100% !important; table-layout: fixed !important; margin: 0 !important; border: 1px solid #000 !important; border-collapse: collapse !important; font-size: 6.5pt !important; color: #000 !important; }
                    
                    /* position: static fixes the missing borders on table headers during print */
                    .premium-table th { position: static !important; }
                    
                    /* Force pure white background and solid black borders on all cells and headers */
                    .premium-table th, .premium-table td { padding: 4px 2px !important; border: 1px solid #000 !important; color: #000 !important; background: transparent !important; white-space: normal !important; word-break: break-word !important; min-width: 0 !important; width: auto !important; vertical-align: middle; text-align: center !important; font-weight: normal !important; }
                    
                    /* Override any inner span or div colors and remove bold text */
                    .premium-table th *, .premium-table td * { color: #000 !important; background: transparent !important; white-space: normal !important; text-align: center !important; font-weight: normal !important; letter-spacing: 0 !important; }
                    
                    .col-menu-trigger, .summary-card, .filter-dropdown, .export-btn { display: none !important; }
                    
                    @page { margin: 5mm; size: landscape; }
                }
            `}</style>
        </div>
    );
};

export default StockBalancePage;
